import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from './client'
import { useTelemetryStore } from '../stores/telemetryStore'

interface OutboundCallRequest {
  phone: string
  name?: string
  agent_type?: string
  assistant_id?: string
  /** Purpose key from the call-purpose registry. Backend resolves this to the
   *  matching VAPI assistant id; takes precedence over the default outbound
   *  assistant but is overridden by an explicit assistant_id. */
  purpose?: string
}

interface OutboundCallResponse {
  success: boolean
  call_id: string
  message: string
  /** Provider mode that handled the dial. Optional because the backend
   *  outbound endpoint doesn't currently stamp this on the response body
   *  (only on the emitted CallEvent payload). Forward-compatible — a
   *  future backend tweak that adds ``"mode": "vapi" | "retell"`` will
   *  be picked up automatically by the consumers that pass this through
   *  to ``registerPendingCall``. */
  mode?: string
}

interface EndCallResponse {
  success: boolean
  call_id: string
}

export function useMakeCall() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: OutboundCallRequest) =>
      apiFetch<OutboundCallResponse>('/vapi/calls/outbound', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calls'] })
      queryClient.invalidateQueries({ queryKey: ['stats'] })
    },
  })
}

export function useEndCall() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (callId: string) =>
      apiFetch<EndCallResponse>(`/vapi/calls/${callId}`, { method: 'DELETE' }),
    onSuccess: (_data, callId) => {
      // Optimistic update: mark the call as ended immediately in the store
      // rather than waiting for the SSE event (which adds seconds of latency).
      // The SSE CALL_ENDED event will be deduplicated by the store's idempotency logic.
      useTelemetryStore.getState().handleEvent('call.ended', {
        call_sid: callId,
        payload: { ended_by: 'user', ended_reason: 'cancelled_by_ui' },
      })
      queryClient.invalidateQueries({ queryKey: ['calls'] })
      queryClient.invalidateQueries({ queryKey: ['stats'] })
    },
  })
}
